import PropTypes from 'prop-types';
import { useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import LoadingScreen from '@/components/loading-screen';
import { useNotify } from '@/components/snackbar';
import { validateImpersonatedAccountAccess } from '@/services/team';
import {
  consumeImpersonationRevokedMessage,
  IMPERSONATION_REVOKED_MESSAGE,
  IMPERSONATION_REVOKED_TITLE,
  isImpersonationAccessError,
} from './impersonationSession';

export default function ImpersonationGuard({ children, allowImpersonating = true }) {
  const { impersonatedUserId, revokeImpersonation } = useImpersonation();
  const isImpersonating = !!impersonatedUserId;
  const notify = useNotify();

  const notifyRevokedAccess = useCallback(
    (message = IMPERSONATION_REVOKED_MESSAGE) => {
      notify.warning(IMPERSONATION_REVOKED_TITLE, {
        subtitle: message,
        autoHideDuration: 8000,
      });
    },
    [notify]
  );

  const safelyRevokeAccess = useCallback(
    (message = IMPERSONATION_REVOKED_MESSAGE) => {
      revokeImpersonation({ message });
    },
    [revokeImpersonation]
  );

  useEffect(() => {
    const revokedMessage = consumeImpersonationRevokedMessage();
    if (revokedMessage) {
      notifyRevokedAccess(revokedMessage);
    }
  }, [notifyRevokedAccess]);

  const { isFetching } = useQuery({
    queryKey: ['impersonation-access', impersonatedUserId],
    queryFn: () => validateImpersonatedAccountAccess({ ownerId: impersonatedUserId }),
    enabled: allowImpersonating && isImpersonating,
    staleTime: 0,
    cacheTime: 0,
    retry: false,
    onSuccess: (hasAccess) => {
      if (!hasAccess) {
        safelyRevokeAccess();
      }
    },
    onError: (error) => {
      if (isImpersonationAccessError(error)) {
        safelyRevokeAccess();
      }
    },
  });

  if (!allowImpersonating && isImpersonating) {
    return <Navigate to="/403" replace />;
  }

  if (allowImpersonating && isImpersonating && isFetching) {
    return <LoadingScreen />;
  }

  return children;
}

ImpersonationGuard.propTypes = {
  children: PropTypes.node,
  allowImpersonating: PropTypes.bool,
};
